import { computed, unref, type MaybeRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { isNil } from 'lodash-es';
import type { AxiosResponse } from 'axios';
import type { hotOffers } from 'shared';

import { api } from '@/shared/api';
import { mapOffersResults } from '@/shared/lib';

export function getHotCitiesQueryOptions({
  origCityCode,
  destCountryCode,
}: {
  origCityCode: MaybeRef<string>;
  destCountryCode: MaybeRef<string | null>;
}) {
  return {
    queryKey: ['hot-offers', 'cities', origCityCode, destCountryCode],
    queryFn: () => {
      return api.hotOffers.cities({
        origCity: unref(origCityCode),
        destCountry: unref(destCountryCode),
      });
    },
    enabled: computed(() => !isNil(unref(destCountryCode))),
    staleTime: Infinity,
  };
}

export function useHotCitiesQuery({
  origCityCode,
  destCountryCode,
}: {
  origCityCode: MaybeRef<string>;
  destCountryCode: MaybeRef<string | null>;
}) {
  const hotCitiesQuery = useQuery<AxiosResponse<hotOffers.HotOffersResponse>>(
    getHotCitiesQueryOptions({ destCountryCode, origCityCode }),
  );

  const hotCities = computed(
    () => hotCitiesQuery.data.value?.data.results.map(mapOffersResults) ?? [],
  );

  const isCitiesLoading = computed(() => hotCitiesQuery.isLoading.value);

  return {
    hotCities,
    isCitiesLoading,
  };
}
